import { format } from 'date-fns-tz';
import { pentagonAreaLines, pentagonAreas } from './constants';
import { utcToSite } from '../../../../../datetimeUtils';

export const getPentagonAreaLines = (duvalType) =>
  pentagonAreaLines[duvalType] || pentagonAreaLines[1];

export const getPentagonAreas = (duvalType) => pentagonAreas[duvalType] || pentagonAreas[1];

function getPolygonCentroid(points) {
  let xSum = 0;
  let ySum = 0;
  let area = 0;

  const n = points.length;

  for (let index = 0; index < n; index += 1) {
    const index_ = (index + 1) % n;
    const cross = points[index].x * points[index_].y - points[index_].x * points[index].y;
    xSum += (points[index].x + points[index_].x) * cross;
    ySum += (points[index].y + points[index_].y) * cross;
    area += cross;
  }

  area *= 0.5;
  const factor = 1 / (6 * area);

  return {
    x: xSum * factor,
    y: ySum * factor,
  };
}

export function getGasCentroid(gasses) {
  const gassesSum = gasses.reduce((acc, current) => acc + current, 0);
  const gasPoints = gasses.map((gas, index) => {
    const relatedGas = gas / gassesSum;
    const relatedGasPercentage = relatedGas * 100;
    const gasAngle = 90 - 72 * index;
    return {
      x: Number.parseFloat(
        (relatedGasPercentage * Math.cos((gasAngle * Math.PI) / 180) * -1).toFixed(10)
      ),
      y: Number.parseFloat(
        (relatedGasPercentage * Math.cos(((gasAngle - 90) * Math.PI) / 180) * -1).toFixed(10)
      ),
    };
  });
  return getPolygonCentroid(gasPoints);
}

export const normalizeCoordinate = (coord) => (coord / 40) * 50;

export function getCurrentPentagonArea({
  h2,
  c2h6,
  ch4,
  c2h4,
  c2h2,
  duvalType,
  pentagonWidth,
  pentagonHeight,
}) {
  const currentArea = getPentagonAreas(duvalType).find((area) => {
    const polyCorners = area.points.length;
    const polyX = area.points.map(
      (point) => pentagonWidth / 2 + pentagonWidth * (normalizeCoordinate(point[0]) / 100)
    );
    const polyY = area.points.map(
      (point) => pentagonHeight / 2 + pentagonHeight * (normalizeCoordinate(point[1]) / 100)
    );

    let index = polyCorners - 1;

    const gasses = [h2, c2h6, ch4, c2h4, c2h2];

    const centroidCoordinates = getGasCentroid(gasses);

    const x =
      pentagonWidth / 2 + pentagonWidth * (normalizeCoordinate(centroidCoordinates.x) / 100);
    const y =
      pentagonHeight / 2 + pentagonHeight * (normalizeCoordinate(centroidCoordinates.y) / 100);

    let oddNodes = false;

    for (let index_ = 0; index_ < polyCorners; index_ += 1) {
      if (
        ((polyY[index_] < y && polyY[index] >= y) || (polyY[index] < y && polyY[index_] >= y)) &&
        polyX[index_] +
          ((y - polyY[index_]) / (polyY[index] - polyY[index_])) * (polyX[index] - polyX[index_]) <
          x
      ) {
        oddNodes = !oddNodes;
      }
      index = index_;
    }

    return oddNodes;
  });

  return currentArea?.name?.slice(0, 2) || 'Un';
}

export const tooltipLabelFormatter = (time, timezone) => {
  const dateFormat = 'yyyy-MM-dd, HH:mm:ss';
  return format(utcToSite(time, timezone), dateFormat, { timeZone: timezone });
};

export const darkenHex = (color, amount) => {
  let R = Number.parseInt(color[1] + color[2], 16);
  let G = Number.parseInt(color[3] + color[4], 16);
  let B = Number.parseInt(color[5] + color[6], 16);

  R = R > amount ? R - amount : 0o0;
  G = G > amount ? G - amount : 0o0;
  B = B > amount ? B - amount : 0o0;

  const RHex = R > 16 ? R.toString(16) : `0${R.toString(16)}`;
  const BHex = B > 16 ? B.toString(16) : `0${B.toString(16)}`;
  const GHex = G > 16 ? G.toString(16) : `0${G.toString(16)}`;

  const darkenedColor = `#${RHex}${GHex}${BHex}`;
  return darkenedColor;
};
